// AgentDataSample (AIAgent_AgentDataSamples) sub-entity client — restores the "Data Samples"
// Agent tab dropped in the 2026-08-19/20 AgentEditor.tsx refactor. Targets the real live backend
// (BizFirst.Ai.AiAgent.Api.Base's BaseAgentDataSampleController), verified by reading the
// controller and entity directly.
//
// ROUTE DRIFT FOUND — the task's own briefing guessed `/api/v1/ai-agent/data-samples` ("verify
// exact path"). Reading BaseAgentDataSampleController.cs directly shows
// `[Route("api/v1/ai-agent/agent-data-samples")]` — `/agent-data-samples`.
//
// Fields confirmed exactly as briefed: AgentDataSample.cs has Name/Description/Content (one
// free-form ntext field)/IsActive — no input/output split, no SampleType enum.
//
// Route-shape quirk confirmed exactly as briefed: Update and Delete are `POST update`/
// `POST delete` (both still bound to the generic UpdateWebRequest/DeleteWebRequest DTOs, just
// exposed over POST instead of the generic PUT {id}/DELETE {id} every sibling controller here
// uses) — not a differently-shaped body, just a different HTTP verb + path. `updateDataSample`
// reuses `post()` (POST + Data-nested, matching UpdateWebRequest.Data) and `deleteDataSample`
// reuses `postQuery()` (POST + root-spread, matching DeleteWebRequest.ID the same way `del()`
// does for every generic-DELETE sibling) — no new base-client method needed.
//
// getDataSamplesByAgent posts to `by-agent-id` (confirmed on BaseAgentDataSampleController.cs,
// bound to its own AgentDataSampleRequests.GetByAgentIdSearchRequest — a per-entity type, but
// carrying the same `AgentID: IDInfo` field as the shared GetByAgentSearchRequest every other
// entity here uses), same `{ AgentID: { ID } }` shape.

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { AgentDataSampleDTO, AgentDataSampleFormValues } from '../types';

export class AgentDataSampleAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  async getDataSamplesByAgent(agentId: number): Promise<AdminApiResult<AgentDataSampleDTO[]>> {
    const result = await this.postQuery<AgentDataSampleDTO[]>('/api/v1/ai-agent/agent-data-samples/by-agent-id', {
      AgentID: { ID: agentId },
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async createDataSample(agentId: number, sample: AgentDataSampleFormValues): Promise<AdminApiResult<AgentDataSampleDTO>> {
    return this.post<AgentDataSampleDTO>('/api/v1/ai-agent/agent-data-samples', { AgentID: agentId, ...sample });
  }

  /** `POST update`, not `PUT {id}` — see this file's header comment. */
  async updateDataSample(agentDataSampleId: number, sample: Partial<AgentDataSampleFormValues>): Promise<AdminApiResult<AgentDataSampleDTO>> {
    return this.post<AgentDataSampleDTO>('/api/v1/ai-agent/agent-data-samples/update', {
      AgentDataSampleID: agentDataSampleId,
      ...sample,
    });
  }

  /** `POST delete`, not `DELETE {id}` — see this file's header comment. */
  async deleteDataSample(agentDataSampleId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-agent/agent-data-samples/delete', { id: agentDataSampleId });
  }
}
